import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Edit } from 'react-feather';
import { GetOrderQuery as GetOrder, Permission } from '__generated__/graphql';
import { useSession } from '../../../Auth/SessionProvider';
import EditExtraInfoForm from './EditExtraInfoForm';
import ParticipantFieldValue from '../../../../common/ParticipantFields/ParticipantFieldValue';
import UI from '../../../../common/UI';
import useProject from '../../../useProject';

type Order = GetOrder['order'];

export interface ExtraInfoSectionProps {
  order: Order;
}

const ExtraInfoSection = ({ order }: ExtraInfoSectionProps) => {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const project = useProject();

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  const [showForm, setShowForm] = useState(false);

  const handleSuccess = () => {
    setShowForm(false);
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  return (
    <UI.Div>
      {!showForm && (
        <UI.FadeIn>
          <UI.GridContainer sc={{ columns: ['1fr', '2fr 1fr'] }}>
            <UI.GridContainer>
              {order.participant_fields.map((field) => (
                <ParticipantFieldValue
                  fieldEntry={{
                    value: null,
                    choice_entries: [],
                    ...order.participant_field_entries.filter(
                      (fieldEntry) => fieldEntry.field.id === field.id,
                    )[0],
                  }}
                  field={field}
                  key={field.id}
                />
              ))}
              {order.stand_alone_upgrades.map((upgrade) => (
                <Fragment key={upgrade.id}>
                  <UI.HR />
                  <UI.Legend>
                    <UI.Delimit>
                      {upgrade.product.title}
                      {upgrade.promotion.title}
                      {upgrade.product_variant?.title}
                    </UI.Delimit>
                  </UI.Legend>
                  {upgrade.participant_fields.map((field) => (
                    <ParticipantFieldValue
                      fieldEntry={{
                        value: null,
                        choice_entries: [],
                        ...upgrade.participant_field_entries.filter(
                          (fieldEntry) => fieldEntry.field.id === field.id,
                        )[0],
                      }}
                      field={field}
                      key={field.id}
                    />
                  ))}
                </Fragment>
              ))}
            </UI.GridContainer>
            {hasEditPermission && (
              <UI.Div sc={{ textAlign: ['left', 'right'] }}>
                <UI.Button
                  onClick={() => setShowForm(true)}
                  sc={{ blank: true, size: 'sm', my: -1 }}
                  aria-label={t('edit_extra_information')}
                >
                  <UI.Icon>
                    <Edit />
                  </UI.Icon>
                  {' '}
                  {t('edit')}
                </UI.Button>
              </UI.Div>
            )}
          </UI.GridContainer>
        </UI.FadeIn>
      )}
      {showForm && (
        <UI.FadeIn>
          <UI.GridContainer sc={{ columns: ['1fr', '2fr 1fr'] }}>
            <EditExtraInfoForm
              order={order}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          </UI.GridContainer>
        </UI.FadeIn>
      )}
    </UI.Div>
  );
};

export default ExtraInfoSection;
